/**
 * FastSimonSearchModal
 *
 * Replicates the SearchSpring/Searchanise-style 3-column autocomplete modal
 * (Popular + Recent | Facets | Products | Useful Links) using Fast Simon SDK.
 *
 * Data sources:
 *   - Popular searches  → window.FastSimonSDK.instantSearch()     response.payload.popularSearches
 *   - Recent searches   → localStorage (SDK has no recent-search store)
 *   - Facets + products → window.FastSimonSDK.fullTextSearch()    response.payload.{facets,products}
 *   - Useful links      → hardcoded in the HTML template
 */
class FastSimonSearchModal {
    constructor(options = {}) {
        this.opts = Object.assign({
            inputSelector:     '#fs-search-input',
            modalSelector:     '#fs-search-modal',
            searchPagePath:    '/collections/shop',  // Thomas Sabo SERP path
            queryParam:        'q',
            productsPerPage:   6,
            maxPopular:        6,
            maxRecent:         6,
            // Only render these facet keys inside the modal. Null = show whatever the SDK returns.
            allowedFacets:     null,
            debounceMs:        250,
            recentStorageKey:  'fs_recent_searches',
            maxStoredRecent:   10,
            // Optional hook — takes (term, narrowBy) and returns a URL.
            // Override if your FastSimon SERP uses a specific filter-in-URL convention.
            buildSerpUrl:      null,
            currency:          'AUD',  // for the $ symbol in price rendering
            defaultTerm:       null,   // term used on focus if SDK returns no popular searches
            defaultPopularSearches: null, // hardcoded popular searches if SDK returns none
        }, options);

        this.state = {
            query: '',
            narrowBy: [],         // [[facetKey, value], ...]
            products: [],
            facets: [],
            popularSearches: [],
            totalResults: 0,
            isOpen: false,
            // Term that the current state.facets was fetched for. Facets are
            // intentionally locked across hover-filter changes — they only
            // refresh when the query itself changes (typing or default-on-focus).
            facetsLockedForQuery: null,
        };

        this._debounceTimer = null;
        this._requestId = 0;     // simple in-flight invalidation
        this._sdkReady = false;

        // Bind handlers so we can remove them on destroy()
        this._onInput        = this._onInput.bind(this);
        this._onFocus        = this._onFocus.bind(this);
        this._onDocumentClick = this._onDocumentClick.bind(this);
        this._onKeydown      = this._onKeydown.bind(this);
        this._onSubmit       = this._onSubmit.bind(this);
    }

    // ---------- Lifecycle ----------

    init() {
        this.inputs = Array.from(document.querySelectorAll(this.opts.inputSelector));
        this.modal = document.querySelector(this.opts.modalSelector);
        if (!this.inputs.length || !this.modal) {
            console.warn('[FS Search] input or modal element not found', this.opts);
            return this;
        }
        // Default to first input; updated on focus/input to whichever is active
        this.input = this.inputs[0];

        // Move the modal to <body> so it can't be clipped by an ancestor's
        // overflow:hidden, and so position:fixed is anchored to the viewport
        // instead of a transformed ancestor's containing block.
        if (this.modal.parentElement !== document.body) {
            document.body.appendChild(this.modal);
        }

        // Bind resize handler so the modal follows the input if the window changes
        this._onResize = this._updatePosition.bind(this);

        // Attach events to every matching input
        this.inputs.forEach(inp => {
            inp.addEventListener('input', this._onInput);
            inp.addEventListener('focus', this._onFocus);
            const form = inp.closest('form');
            if (form && !form._fsSubmitBound) {
                form.addEventListener('submit', this._onSubmit);
                form._fsSubmitBound = true;
            }
        });
        document.addEventListener('click', this._onDocumentClick);
        document.addEventListener('keydown', this._onKeydown);
        window.addEventListener('resize', this._onResize);

        // Prefetch popular searches so column 1 is populated on first focus
        this._whenSdkReady(() => this._fetchPopularSearches(''));
        return this;
    }

    destroy() {
        this.inputs?.forEach(inp => {
            inp.removeEventListener('input', this._onInput);
            inp.removeEventListener('focus', this._onFocus);
        });
        document.removeEventListener('click', this._onDocumentClick);
        document.removeEventListener('keydown', this._onKeydown);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    _updatePosition() {
        if (!this.input || !this.modal) return;
        const rect = this.input.getBoundingClientRect();
        this.modal.style.top = Math.round(rect.bottom) + 'px';
    }

    // ---------- Event handlers ----------

    _onInput(e) {
        this.input = e.target;  // track whichever input is being typed in
        const q = e.target.value.trim();
        if (q !== this.state.query) {
            // Query changed — facets need a refresh on next fetch
            this.state.facetsLockedForQuery = null;
            this.state.narrowBy = [];
        }
        this.state.query = q;
        clearTimeout(this._debounceTimer);
        this._debounceTimer = setTimeout(() => this._runSearch(), this.opts.debounceMs);
        if (!this.state.isOpen) this.open();
    }

    _onFocus(e) {
        this.input = e.target;  // track whichever input received focus
        this.open();
        this._renderRecent();
        this._whenSdkReady(() => {
            if (!this.state.popularSearches.length) {
                window.FastSimonSDK.instantSearch({
                    query: '',
                    callback: (resp) => {
                        const popular = (resp && resp.payload && resp.payload.popularSearches) || [];
                        this.state.popularSearches = popular.slice(0, this.opts.maxPopular);
                        this._renderPopular();
                        this._defaultFetchIfEmpty();
                    }
                });
            } else {
                this._defaultFetchIfEmpty();
            }
        });
    }

    _defaultFetchIfEmpty() {
        if (this.input && this.input.value.trim()) return;

        const facetTerm = this.opts.defaultTerm || 'charms';

        // Fetch popular products (SDK returns trending products for '*')
        this.state.query = '*';
        window.FastSimonSDK.fullTextSearch({
            term: '*',
            page: 1,
            productsPerPage: this.opts.productsPerPage,
            narrowBy: [],
            facets: true,
            onFacetsLoaded: () => {},
            onProductsLoaded: (resp) => {
                const root = resp || {};
                const data = (Array.isArray(root.products) || Array.isArray(root.facets))
                    ? root : (root.payload || {});
                if (Array.isArray(data.products)) {
                    this.state.products = data.products.filter(p => !p.promotile);
                    if (typeof data.totalResults === 'number') {
                        this.state.totalResults = data.totalResults;
                    }
                    this._renderProducts();
                    this._renderSeeAllLink();
                }
            }
        });

        // Fetch facets from a real search term (SDK returns no facets for '*')
        this.state.facetsLockedForQuery = null;
        window.FastSimonSDK.fullTextSearch({
            term: facetTerm,
            page: 1,
            productsPerPage: 1,
            narrowBy: [],
            facets: true,
            onProductsLoaded: () => {},
            onFacetsLoaded: (resp) => {
                const root = resp || {};
                const data = (Array.isArray(root.products) || Array.isArray(root.facets))
                    ? root : (root.payload || {});
                if (Array.isArray(data.facets)) {
                    this.state.facets = data.facets;
                    this.state.facetsLockedForQuery = '*';
                    this._renderFacets();
                }
            }
        });
    }

    _onDocumentClick(e) {
        if (!this.state.isOpen) return;
        // Check against __inner (the content area), not the full-width modal.
        // Clicks on the backdrop or outside the content should close the modal.
        const inner = this.modal.querySelector('.fs-search-modal__inner');
        if (inner && inner.contains(e.target)) return;
        if (this.inputs.some(inp => inp.contains(e.target))) return;
        this.close();
    }

    _onKeydown(e) {
        if (e.key === 'Escape' && this.state.isOpen) this.close();
    }

    _onSubmit() {
        if (this.state.query) this._addRecent(this.state.query);
        // Let the form submit naturally to /collections/shop?q=...
    }

    // ---------- SDK readiness ----------

    _whenSdkReady(cb) {
        if (this._sdkReady || (window.FastSimonSDK && typeof window.FastSimonSDK.instantSearch === 'function')) {
            this._sdkReady = true;
            cb();
            return;
        }
        const id = setInterval(() => {
            if (window.FastSimonSDK && typeof window.FastSimonSDK.instantSearch === 'function') {
                clearInterval(id);
                this._sdkReady = true;
                cb();
            }
        }, 50);
    }

    // ---------- Search orchestration ----------

    _runSearch() {
        const myId = ++this._requestId;
        this._whenSdkReady(() => {
            this._fetchPopularSearches(this.state.query, myId);
            if (this.state.query.length >= 1) {
                this._fetchSerp(this.state.query, myId);
            } else {
                // Query cleared — restore the default "on focus" view
                // (popular term products + filters) instead of showing nothing.
                this.state.facetsLockedForQuery = null;
                this._defaultFetchIfEmpty();
            }
        });
    }

    _fetchPopularSearches(query, myId = this._requestId) {
        window.FastSimonSDK.instantSearch({
            query: query || '',
            callback: (resp) => {
                if (myId !== this._requestId) return;
                const popular = (resp && resp.payload && resp.payload.popularSearches) || [];
                this.state.popularSearches = popular.slice(0, this.opts.maxPopular);
                this._renderPopular();
            }
        });
    }

    _fetchSerp(term, myId = this._requestId) {
        // SDK response shapes differ between methods: instantSearch nests data
        // under .payload, but fullTextSearch callbacks deliver products/facets
        // at the response root. Handle both so minor SDK-version drift doesn't
        // silently break rendering.
        let reported = false;
        const handler = (resp) => {
            if (myId !== this._requestId) return;
            const root = resp || {};
            // Prefer root keys, fall back to .payload if SDK wrapped it
            const data = (Array.isArray(root.products) || Array.isArray(root.facets))
                ? root
                : (root.payload || {});

            if (Array.isArray(data.products)) {
                this.state.products = data.products.filter(p => !p.promotile);
                if (typeof data.totalResults === 'number') {
                    this.state.totalResults = data.totalResults;
                }
                this._renderProducts();
                this._renderSeeAllLink();
                if (!reported) {
                    this._reportSearchPerformed(term, data);
                    reported = true;
                }
            }

            if (Array.isArray(data.facets) && this.state.facetsLockedForQuery !== term) {
                // Facets refresh only on query change. Subsequent fetches for the
                // same term (hover-filter previews) leave facets alone so the list
                // doesn't visually shuffle as filters are applied.
                this.state.facets = data.facets;
                this.state.facetsLockedForQuery = term;
                this._renderFacets();
            }
        };

        window.FastSimonSDK.fullTextSearch({
            term: term,
            page: 1,
            productsPerPage: this.opts.productsPerPage,
            narrowBy: this.state.narrowBy,
            facets: true,
            onFacetsLoaded:   handler,
            onProductsLoaded: handler,
        });
    }

    _reportSearchPerformed(term, payload) {
        try {
            window.FastSimonSDK.event({
                eventName: window.FastSimonEventName.SearchPerformed,
                data: {
                    query: term,
                    narrowBy: this.state.narrowBy,
                    products: this.state.products.map(p => p.id).join(','),
                    rescount: payload.totalResults,
                    pagenum: payload.page || 1,
                }
            });
        } catch (_) { /* eventing is non-critical */ }
    }

    // ---------- Open/close ----------

    open() {
        this._updatePosition();
        this.modal.classList.add('fs-search-modal--open');
        this.state.isOpen = true;
    }

    close() {
        this.modal.classList.remove('fs-search-modal--open');
        this.state.isOpen = false;
    }

    // ---------- Rendering ----------

    _renderPopular() {
        const el = this.modal.querySelector('[data-fs-popular]');
        if (!el) return;
        if (!this.state.popularSearches.length) {
            el.innerHTML = '';
            return;
        }
        el.innerHTML = this.state.popularSearches.map(item => {
            const term = typeof item === 'string' ? item : (item && (item.l || item.term) || '');
            if (!term) return '';
            const href = this._buildSerpUrl(term, []);
            return `<div class="fs-search-modal__term" role="listitem">
              <a href="${this._esc(href)}" data-fs-popular-term="${this._esc(term)}"><em>${this._esc(term)}</em></a>
            </div>`;
        }).join('');

        el.querySelectorAll('[data-fs-popular-term]').forEach(a => {
            // Hover: preview products + filters for this term
            a.addEventListener('mouseenter', () => {
                const t = a.getAttribute('data-fs-popular-term');
                this._previewTerm(t);
            });
            // Click: save to recent + fire event
            a.addEventListener('click', () => {
                const t = a.getAttribute('data-fs-popular-term');
                this._addRecent(t);
                try {
                    window.FastSimonSDK.event({
                        eventName: window.FastSimonEventName.AutocompletePopularClicked,
                        data: { query: this.state.query, term: t }
                    });
                } catch (_) {}
            });
        });
    }

    _renderRecent() {
        const el = this.modal.querySelector('[data-fs-recent]');
        const header = this.modal.querySelector('[data-fs-recent-header]');
        if (!el) return;
        const recent = this._getRecent();
        if (!recent.length) {
            el.innerHTML = '';
            if (header) header.style.display = 'none';
            return;
        }
        if (header) header.style.display = '';
        el.innerHTML = recent.slice(0, this.opts.maxRecent).map(term => {
            const href = this._buildSerpUrl(term, []);
            return `<div class="fs-search-modal__term" role="listitem">
              <a href="${this._esc(href)}" data-fs-recent-term="${this._esc(term)}"><em>${this._esc(term)}</em></a>
            </div>`;
        }).join('');

        el.querySelectorAll('[data-fs-recent-term]').forEach(a => {
            a.addEventListener('mouseenter', () => {
                const t = a.getAttribute('data-fs-recent-term');
                this._previewTerm(t);
            });
        });
    }

    _renderFacets() {
        const el = this.modal.querySelector('[data-fs-facets]');
        if (!el) return;
        if (!this.state.facets.length || !this.state.query) {
            el.innerHTML = '';
            return;
        }
        const allowed = this.opts.allowedFacets;
        const facets = allowed
            ? this.state.facets.filter(f => allowed.includes(f[0]))
            : this.state.facets;

        if (allowed && this.state.facets.length && !facets.length) {
            // Loud diagnostic: SDK returned facets but our whitelist matched none.
            // Probably a key mismatch — log the keys so config can be fixed.
            console.warn(
                '[FS Modal] allowedFacets filtered out all facets for query "' + this.state.query +
                '". SDK returned keys:', this.state.facets.map(f => f[0]),
                'Whitelist:', allowed
            );
            el.innerHTML = '';
            return;
        }

        // Collect display labels so _buildSerpUrl can map key → label
        if (!this.state.facetLabels) this.state.facetLabels = {};
        facets.forEach(f => { this.state.facetLabels[f[0]] = f[2] || f[0]; });

        const activeKey = this.state.narrowBy[0]
            ? this.state.narrowBy[0][0] + '\u0000' + this.state.narrowBy[0][1]
            : null;

        el.innerHTML = facets.map(facet => {
            const key     = facet[0];
            const options = facet[1] || [];
            const label   = facet[2] || key;
            return `<div class="fs-search-modal__facet">
              <h5 class="fs-search-modal__heading">${this._esc(label)}</h5>
              <div class="fs-search-modal__facet-options">
                ${options.map(opt => {
                    const value    = opt[0];
                    const isActive = (key + '\u0000' + value) === activeKey;
                    const href     = this._buildSerpUrl(this.state.query, [[key, value]]);
                    return `<a class="fs-search-modal__facet-option${isActive ? ' is-active' : ''}"
                      href="${this._esc(href)}"
                      data-facet-key="${this._esc(key)}"
                      data-facet-value="${this._esc(value)}"
                      aria-label="filter by ${this._esc(label)} - ${this._esc(value)}">${this._esc(value)}</a>`;
                }).join('')}
              </div>
            </div>`;
        }).join('');

        // Hover applies the (single, mutually-exclusive) preview filter.
        // Filter persists when the cursor moves away — only changes when
        // another facet option is hovered.
        el.querySelectorAll('[data-facet-key]').forEach(node => {
            node.addEventListener('mouseenter', () => {
                this._previewFilter(node.dataset.facetKey, node.dataset.facetValue);
            });
        });
    }

    _previewFilter(key, value) {
        // No-op if it's already the active preview
        const cur = this.state.narrowBy[0];
        if (cur && cur[0] === key && cur[1] === value) return;

        this.state.narrowBy = [[key, value]];

        // Immediate visual feedback — toggle is-active without waiting for refetch
        const facetsEl = this.modal.querySelector('[data-fs-facets]');
        if (facetsEl) {
            facetsEl.querySelectorAll('[data-facet-key]').forEach(n => {
                const isActive = n.dataset.facetKey === key && n.dataset.facetValue === value;
                n.classList.toggle('is-active', isActive);
            });
        }

        // Debounced fetch so flying through options doesn't spam the API
        clearTimeout(this._hoverFetchTimer);
        this._hoverFetchTimer = setTimeout(() => {
            if (this.state.query) this._fetchSerp(this.state.query, ++this._requestId);
        }, 150);
    }

    _previewTerm(term) {
        if (!term || term === this.state.query) return;
        this.state.query = term;
        this.state.narrowBy = [];
        this.state.facetsLockedForQuery = null;  // refresh facets for the new term
        clearTimeout(this._hoverFetchTimer);
        this._hoverFetchTimer = setTimeout(() => {
            this._fetchSerp(term, ++this._requestId);
        }, 150);
    }

    _renderProducts() {
        if (this.state.products[0]) console.log('[FS autocomplete product]', this.state.products[0]);
        console.log('[FS keys]', Object.keys(this.state.products[0]));
        console.log('[FS variants?]', this.state.products[0].variants, this.state.products[0].vars, this.state.products[0].v);
        const el = this.modal.querySelector('[data-fs-products]');
        if (!el) return;
        if (!this.state.products.length) {
            el.innerHTML = this.state.query
                ? `<div class="fs-search-modal__empty">No matches for "${this._esc(this.state.query)}"</div>`
                : '';
            return;
        }
        el.innerHTML = this.state.products.map((p, i) => {
            const img = p.t || p.t2 || 'https://acp-magento.appspot.com/images/missing.gif';
            return `<article class="fs-search-modal__product" data-product-id="${this._esc(p.id)}" data-position="${i + 1}">
              <a class="fs-search-modal__product-link" href="${this._esc(p.u || '#')}">
                <div class="fs-search-modal__product-image-wrap">
                  <img class="fs-search-modal__product-image" src="${this._esc(img)}" alt="${this._esc(p.l || '')}" loading="lazy">
                </div>
                <div class="fs-search-modal__product-title">${this._esc(p.l || '')}</div>
                ${this._formatPrice(p)}
              </a>
            </article>`;
        }).join('');

        el.querySelectorAll('[data-product-id]').forEach(node => {
            node.addEventListener('click', () => {
                try {
                    window.FastSimonSDK.event({
                        eventName: window.FastSimonEventName.AutocompleteProductClicked,
                        data: {
                            query:     this.state.query,
                            productID: node.dataset.productId,
                            position:  Number(node.dataset.position),
                        }
                    });
                } catch (_) {}
                if (this.state.query) this._addRecent(this.state.query);
            });
        });
    }

    _renderSeeAllLink() {
        const el = this.modal.querySelector('[data-fs-see-all]');
        if (!el) return;
        if (!this.state.query || !this.state.totalResults) {
            el.innerHTML = '';
            return;
        }
        const href = this._buildSerpUrl(this.state.query, this.state.narrowBy);
        el.innerHTML = `<a href="${this._esc(href)}">
          See ${this.state.totalResults} ${this.state.narrowBy.length ? 'filtered ' : ''}results for "${this._esc(this.state.query)}"
          <span class="fs-search-modal__chevron" aria-hidden="true">›</span>
        </a>`;
    }

    // ---------- Helpers ----------

    _buildSerpUrl(term, narrowBy) {
        if (typeof this.opts.buildSerpUrl === 'function') {
            return this.opts.buildSerpUrl(term, narrowBy);
        }
        // SERP convention: query gets a trailing wildcard, spaces become "+"
        // rather than "%20" (form-style encoding).
        const encode = (s) => encodeURIComponent(s == null ? '' : s).replace(/%20/g, '+');
        const queryStr = term || '';
        let url = `${this.opts.searchPagePath}?${this.opts.queryParam}=${encode(queryStr)}`;
        if (narrowBy && narrowBy.length) {
            const labels = this.state.facetLabels || {};
            const parts = narrowBy.map(([k, v]) => {
                const label = labels[k] || k;
                return `${encode(label)},${encode(v)}`;
            });
            url += `&filters=${parts.join(',')}`;
        }
        return url;
    }

    _formatPrice(p) {
        if (p.p == null) return '';
        const sym = (this.opts.currency === 'USD' || this.opts.currency === 'AUD') ? '$' : '';
        const current = `${sym}${p.p}`;

        // FastSimon's p.p_c is product-level — it can come from a different
        // variant than the one whose price is shown in p.p. Re-derive compare
        // from the variant whose Price matches p.p.
        const currentNum = Number(p.p);
        let compareNum = p.p_c != null ? Number(p.p_c) : NaN;

        if (!Number.isNaN(currentNum) && Array.isArray(p.vra)) {
            const matched = p.vra.find(entry => {
                const vPrice = this._parseVariantMoney(this._findVariantAttr(entry[1], 'Price'));
                return !Number.isNaN(vPrice) && Math.abs(vPrice - currentNum) < 0.01;
            });
            if (matched) {
                const vCompare = this._parseVariantMoney(this._findVariantAttr(matched[1], 'Compare at price'));
                compareNum = (!Number.isNaN(vCompare) && vCompare > currentNum) ? vCompare : NaN;
            }
        }

        const hasCompare = !Number.isNaN(compareNum) && compareNum > currentNum;
        if (hasCompare) {
            const compare = `${sym}${compareNum.toFixed(2)}`;
            const savings = (compareNum - currentNum).toFixed(2);
            return `<div class="fs-search-modal__price">
            <span class="fs-search-modal__price-current">${this._esc(current)}</span>
            <span class="fs-search-modal__price-compare">${this._esc(compare)}</span>
            <span class="fs-search-modal__price-savings">Save ${sym}${this._esc(savings)}</span>
            </div>`;
        }
        return `<div class="fs-search-modal__price">
        <span class="fs-search-modal__price-current">${this._esc(current)}</span>
        </div>`;
    }

    _getRecent() {
        try {
            return JSON.parse(localStorage.getItem(this.opts.recentStorageKey)) || [];
        } catch (_) { return []; }
    }

    _addRecent(term) {
        if (!term) return;
        const list = this._getRecent().filter(t => t.toLowerCase() !== term.toLowerCase());
        list.unshift(term);
        try {
            localStorage.setItem(this.opts.recentStorageKey, JSON.stringify(list.slice(0, this.opts.maxStoredRecent)));
        } catch (_) {}
        this._renderRecent();
    }

    _esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, m => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[m]));
    }
    _parseVariantMoney(val) {
        if (val == null) return NaN;
        const match = String(val).replace(/,/g, '').match(/[\d.]+/);
        return match ? parseFloat(match[0]) : NaN;
    }
    _findVariantAttr(attrArr, name) {
        if (!Array.isArray(attrArr)) return null;
        for (const entry of attrArr) {
            if (entry && entry[0] === name) {
                const val = entry[1];
                return Array.isArray(val) ? val[0] : val;
            }
        }
        return null;
    }
}

if (typeof window !== 'undefined') {
    window.FastSimonSearchModal = FastSimonSearchModal;
}